import _ from 'lodash'
import {
  create as mathCreate,
  all as mathAll,
  ConfigOptions,
  isResultSet,
  isMatrix,
  isArray,
} from 'mathjs'
import {getCategoricals} from '@/utils/media'
import {IDataSourceStats} from '@/features/data-sources'
import {ExpressionEditorAutoCompleteProvider} from '../context/auto-complete'
import {IExpressionConfig} from '..'

interface IFunctionDocsEntry {
  documentation?: string
  insertText?: string
}

export interface IEvalExpressionResult {
  result: any
  errors?: {
    message: string
  }[]
}

// TODO: refactor?
const mathConfig: ConfigOptions = {}
const math = mathCreate(mathAll, mathConfig)
math.import(
  {
    equal: function (a: any, b: any) {
      return _.isEqual(a, b)
    },
    unequal: function (a: any, b: any) {
      return !_.isEqual(a, b)
    },
  },
  {override: true}
)

function parseMathResponse(response: any): any {
  let res = response
  if (isArray(res)) {
    res = _.chain(res)
      .map((v) => {
        if (isMatrix(v)) return v.valueOf()
        return v
      })
      .flatten()
      .value()
  }
  return res
}

export const functions = {
  // custom
  anyOf: (...v: any[]) => {
    v = _.flatten(v || [])
    v = parseMathResponse(v)
    return _.filter(v).length > 0
  },
  allOf: (...v: any[]) => {
    v = _.flatten(v || [])
    v = parseMathResponse(v)
    return _.filter(v).length === v.length
  },
  // lodash
  includes: _.includes,
  keys: _.keys,
  flatten: _.flatten,
  abs: Math.abs,
  random: Math.random,
}

const functionDocs: Record<keyof typeof functions, IFunctionDocsEntry | undefined> = {
  // custom
  anyOf: {
    documentation: 'Checks if anything in collection is trueish',
    insertText: 'anyOf(${1:array})',
  },
  allOf: {
    documentation: 'Checks that everything in collection is trueish',
    insertText: 'allOf(${1:array})',
  },
  // lodash
  includes: {
    documentation: 'Checks if target is in collection.',
    insertText: 'includes(${1:collection}, ${2:target})',
  },
  keys: {
    documentation: 'Creates an array of the own enumerable property names of object.',
    insertText: 'keys(${1:object})',
  },
  flatten: {
    documentation: 'Flattens array a single level deep',
    insertText: 'flatten(${1:array})',
  },
  abs: {
    documentation:
      'Returns the absolute value of a number (the value without regard to whether it is positive or negative)',
    insertText: 'abs(${1:value})',
  },
  random: {
    documentation: 'Returns a pseudorandom number between 0 and 1',
    insertText: 'random()',
  },
}

// documentation for functions that are dynamically generated
const extraFunctionDocs: Record<string, IFunctionDocsEntry> = {
  // custom
  anyCat: {
    documentation: 'Gets score from a categorical of any type',
    insertText: 'anyCat(${1:key})',
  },
  // lodash
  get: {
    documentation: 'Gets the property value at path.',
    insertText: 'get(${1:path}, ${2:defaultValue})',
  },
  // mathjs
  mean: {
    documentation: 'Compute the mean value of a list with values.',
    insertText: 'mean(${1:values})',
  },
}

export const expressionFunctionNames = _.chain([functionDocs, extraFunctionDocs])
  .map(_.keys)
  .reduce((a, b) => _.union(a, b))
  .sort()
  .value()

const functionDocsToAutoCompleteSuggestions: (
  args: Parameters<ExpressionEditorAutoCompleteProvider>[number] & {
    docs: Record<string, IFunctionDocsEntry | undefined>
  }
) => ReturnType<ExpressionEditorAutoCompleteProvider> = ({monaco, range, docs}) => {
  return _.chain(docs)
    .keys()
    .map((v) => {
      return {
        range,
        kind: monaco.languages.CompletionItemKind.Function,
        label: v,
        insertText: `${v}(\${1})`,
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        ...docs[v],
      }
    })
    .value()
}

export const fieldStatsToAutoCompleteSuggestions: (
  args: Parameters<ExpressionEditorAutoCompleteProvider>[number] & {
    fieldTypes: IDataSourceStats['fieldTypes']
  }
) => ReturnType<ExpressionEditorAutoCompleteProvider> = (args) => {
  const {monaco, range, fieldTypes} = args
  const vars = _.chain(fieldTypes)
    .toPairs()
    .orderBy(([k]) => k)
    .map(([k, v]) => {
      const item = {
        range,
        kind: monaco.languages.CompletionItemKind.Variable,
        label: k,
        insertText: k,
        detail: v,
      }
      return item
    })
    .value()
  return [...vars]
}

export const defaultAutoCompleteSuggestions: ExpressionEditorAutoCompleteProvider = (args) => {
  return [
    ...functionDocsToAutoCompleteSuggestions({
      ...args,
      docs: functionDocs,
    }),
    ...functionDocsToAutoCompleteSuggestions({
      ...args,
      docs: extraFunctionDocs,
    }),
  ]
}

export function stringifyExpressionConfig(config: IExpressionConfig): string {
  // TODO: params?
  return config.expression
}

export function evalExpression(
  expression: string,
  data: {[key: string]: any}
): IEvalExpressionResult {
  if (!expression)
    return {
      result: undefined,
      errors: [{message: 'no expression provided'}],
    }
  // no trailing semicolon on last line
  expression = _.trimEnd(expression, ';')
  // normalize newlines
  expression = expression.split(/\r\n|\r|\n/g).join('\n')
  const reserved = ['mod', 'to', 'in', 'and', 'xor', 'or', 'not', 'end']
  const parsedData = {...data}
  _.chain(data)
    .toPairs()
    .filter(([k]) => _.includes(reserved, k))
    .forEach(([k, v]) => {
      delete parsedData[k]
      parsedData[`${k}_`] = v
    })
    .value()
  const scope = {
    ...parsedData,
    ...functions,
    get: (path: string, defaultValue?: unknown) => _.get(data, path, defaultValue),
    anyCat: (key: string, defaultValue: number = 0) => {
      const cat = getCategoricals(data)
      const res = _.chain(cat).map(key).compact().flatten().first().value()
      if (res === undefined) return defaultValue
      return res
    },
  }
  try {
    let result = math.evaluate(expression, scope)
    if (isResultSet(result)) {
      result = _.last(_.get(result, 'entries') || [])
    }
    return {
      result,
    }
  } catch (error) {
    // console.error(error);
    return {
      result: undefined,
      errors: [
        {
          message: _.toString(error),
        },
      ],
    }
  }
}

type TExpressionConfigParamValues<TConf extends IExpressionConfig['params']> = {
  [K in keyof TConf]: number
}

export function expressionConfigParamValues<TConf extends IExpressionConfig['params']>(
  conf: TConf
): TExpressionConfigParamValues<TConf> {
  // TODO: hmm
  const res: TExpressionConfigParamValues<TConf> = _.chain(conf)
    .toPairs()
    .map(([k, v]) => {
      if (_.isNumber(v)) {
        throw new Error('already parsed')
      }
      if (v.value === undefined) return
      return [k, v.value]
    })
    .compact()
    .fromPairs()
    .value() as TExpressionConfigParamValues<TConf>
  return res
}
